import { getAuth } from 'firebase/auth'

export const DEBUG_USER_STORAGE_KEY = 'debug-user'

/**
 * JWT `schoolIds` is `null` when the claim is absent (district-wide admin)
 * and an array when present — including `[]`, which rules treat as school-tier.
 */
export function tokenSchoolIdsFromClaims(claims) {
  if (!claims || !Object.prototype.hasOwnProperty.call(claims, 'schoolIds')) return null
  return Array.isArray(claims.schoolIds) ? claims.schoolIds : null
}

/**
 * The live probes use the Firebase Auth JWT. The launcher/header can show
 * `authStore.currentUser.role`, which the Debug Role Switcher overwrites
 * without changing the token. Block the run when those disagree.
 */
export function inspectSmokeAuth({
  jwtRole = null,
  jwtUid = null,
  storeRole = null,
  storeUid = null,
  debugUserRaw = null
} = {}) {
  const reasons = []
  if (debugUserRaw) {
    reasons.push(
      'Debug Role Switcher is active. Probes still use the real JWT — reset the switcher or sign out/in.'
    )
  }
  if (jwtUid && storeUid && jwtUid !== storeUid) {
    reasons.push(`Store uid does not match the signed-in user.`)
  }
  if (jwtRole && storeRole && jwtRole !== storeRole) {
    reasons.push(`Store role (${storeRole}) does not match JWT role (${jwtRole}).`)
  }
  return {
    jwtRole: jwtRole || null,
    storeRole: storeRole || null,
    jwtUid: jwtUid || null,
    storeUid: storeUid || null,
    blocked: reasons.length > 0,
    reasons
  }
}

export function clearDebugUserSpoof() {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(DEBUG_USER_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

export function readDebugUserRaw() {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage.getItem(DEBUG_USER_STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * Poll the Firebase Auth token until `claims.role` matches, or give up.
 * Used by the Demo Role panel and the in-session complete-check sweep so
 * probes never run on a stale JWT.
 */
export function matchesSmokeClaims(claims, role, scope) {
  if (claims.role !== role) return false
  if (!scope) return true
  const expectedAllSchools =
    scope.allSchools === true &&
    ['case_manager', 'case_manager_full', 'speech_case_manager'].includes(role)
  return (
    (claims.allSchools === true) === expectedAllSchools &&
    JSON.stringify(tokenSchoolIdsFromClaims(claims)?.slice().sort() ?? null) ===
      JSON.stringify(scope.schoolIds?.slice().sort() ?? null)
  )
}

export async function waitForJwtRoleClaim(role, { attempts = 30, delayMs = 500, scope } = {}) {
  const auth = getAuth()
  for (let i = 0; i < attempts; i++) {
    if (!auth.currentUser) return false
    try {
      const token = await auth.currentUser.getIdTokenResult(true)
      if (matchesSmokeClaims(token.claims, role, scope)) return true
    } catch {
      /* retry */
    }
    await new Promise((r) => setTimeout(r, delayMs))
  }
  return false
}

export async function loadSmokeAuthInspection(storeUser) {
  const auth = getAuth()
  const user = auth.currentUser
  if (!user) {
    return {
      signedIn: false,
      user: null,
      token: null,
      tokenSchoolIds: null,
      inspection: inspectSmokeAuth({
        storeRole: storeUser?.role || null,
        storeUid: storeUser?.uid || null,
        debugUserRaw: readDebugUserRaw()
      })
    }
  }

  const token = await user.getIdTokenResult()
  return {
    signedIn: true,
    user,
    token,
    tokenSchoolIds: tokenSchoolIdsFromClaims(token.claims),
    inspection: inspectSmokeAuth({
      jwtRole: token.claims.role || null,
      jwtUid: user.uid,
      storeRole: storeUser?.role || null,
      storeUid: storeUser?.uid || null,
      debugUserRaw: readDebugUserRaw()
    })
  }
}
